import type { DataProvider, GetListParams, GetOneParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchInventoryItems } from './fetchInventoryItems';
import type { ApiSort, FetchInventoryItemsVariables, InventoryItemRecord } from './types';

// The list filter values react-admin threads through (it types `filter` as
// `any`); narrowing here keeps the mapping below type-checked.
interface InventoryFilter {
  state?: string;
  condition?: string;
  sku?: string;
  parentSku?: string;
  brand?: string;
  itemId?: string | number;
  searchQuery?: string;
}

// react-admin sort → reware-api sort codes (only ID and update time are supported)
const toApiSort = (sort?: { field: string, order: string }): ApiSort => {
  if (!sort) return 'ID_DESC';
  const direction = sort.order === 'ASC' ? 'ASC' : 'DESC';
  return sort.field === 'updatedAt' ? `UPDATE_${direction}` : `ID_${direction}`;
};

// Coerce the free-text Item ID filter to a number, dropping blanks and non-numeric input.
const toItemId = (value: string | number | undefined): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toApiVariables = (params: GetListParams): FetchInventoryItemsVariables => {
  const { page = 1, perPage = 25 } = params.pagination ?? {};
  const filter = (params.filter ?? {}) as InventoryFilter;

  return {
    partnerId: requirePartner().id,
    page,
    pageSize: perPage,
    sort: toApiSort(params.sort),
    state: filter.state || undefined,
    condition: filter.condition || undefined,
    sku: filter.sku || undefined,
    parentSku: filter.parentSku || undefined,
    brand: filter.brand || undefined,
    itemId: toItemId(filter.itemId),
    searchQuery: filter.searchQuery || undefined,
  };
};

/**
 * Read-only provider: only getList and getOne are supported. The remaining
 * DataProvider methods are stubbed to throw so an unsupported call fails with a
 * clear message rather than an opaque "undefined is not a function" (same stub
 * pattern as src/shopifySync/dataProvider.ts).
 */
interface InventoryItemsDataProvider {
  getList: (
    resource: string,
    params: GetListParams
  ) => Promise<{ data: InventoryItemRecord[], total: number }>;
  getOne: (
    resource: string,
    params: GetOneParams
  ) => Promise<{ data: InventoryItemRecord }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const inventoryItemsDataProviderImpl: InventoryItemsDataProvider = {
  getList: async (_resource, params) => {
    const result = await fetchInventoryItems(toApiVariables(params));
    return { data: result.rows, total: result.count };
  },

  getOne: async (_resource, params) => {
    const result = await fetchInventoryItems({
      partnerId: requirePartner().id,
      page: 1,
      pageSize: 1,
      sort: 'ID_DESC',
      itemId: Number(params.id),
    });
    if (!result.rows.length) {
      throw new Error(`Inventory item ${params.id} not found`);
    }
    return { data: result.rows[0] };
  },

  getMany: async () => { throw new Error('getMany not implemented for inventoryItems resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for inventoryItems resource'); },
  create: async () => { throw new Error('create not implemented for inventoryItems resource'); },
  update: async () => { throw new Error('update not implemented for inventoryItems resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for inventoryItems resource'); },
  delete: async () => { throw new Error('delete not implemented for inventoryItems resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for inventoryItems resource'); },
};

// React-admin's DataProvider methods are generic, which prevents a type-safe
// single-resource provider; we define proper types above and cast here.
export const inventoryItemsDataProvider =
  inventoryItemsDataProviderImpl as unknown as DataProvider;
